/** Pure helpers for the graphical analytics view (equity curve +
 * per-period P&L bars). Kept separate from the SVG so the scaling math is
 * unit-testable without a DOM. */

import type { TimeseriesBucket } from "@/lib/reportsApi";

export interface CumPoint {
  period: string;
  pnl: number;
  cum: number;
}

/** Running total of P&L across buckets (assumed ascending by period). */
export function cumulative(buckets: readonly TimeseriesBucket[]): CumPoint[] {
  let run = 0;
  return buckets.map((b) => {
    run += b.pnl;
    return { period: b.period, pnl: b.pnl, cum: run };
  });
}

export interface Stats {
  totalPnl: number;
  trades: number;
  wins: number;
  winRate: number; // 0..1
}

export function summarize(buckets: readonly TimeseriesBucket[]): Stats {
  let totalPnl = 0;
  let trades = 0;
  let wins = 0;
  for (const b of buckets) {
    totalPnl += b.pnl;
    trades += b.trades;
    wins += b.wins;
  }
  return { totalPnl, trades, wins, winRate: trades === 0 ? 0 : wins / trades };
}

export interface Bounds {
  min: number;
  max: number;
}

/** [min, max] always spanning zero so the baseline is on-chart; widens a
 * degenerate (flat) series so it still renders a line. */
export function boundsWithZero(values: readonly number[]): Bounds {
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  if (min === max) return { min: min - 1, max: max + 1 };
  return { min, max };
}

/** Map a value to a y pixel in [0, height]; max at the top (y=0). */
export function yScale(value: number, bounds: Bounds, height: number): number {
  return height - ((value - bounds.min) / (bounds.max - bounds.min)) * height;
}

/** Evenly spaced x for index i of n points across width. Single point sits
 * at the left edge. */
export function xScale(i: number, n: number, width: number): number {
  if (n <= 1) return 0;
  return (i / (n - 1)) * width;
}
